import type { Montageunterlage } from "@/lib/projectOverview";
import { isAufmassPackItem, isWerkstattPackItem } from "@/lib/projectOverview";
import {
  isWerkstattStueckliste,
  pickLatestStuecklisten,
  stuecklisteDisplayNumber,
  type Stueckliste,
} from "@/lib/stuecklisten";

export type DocumentPackKind = "montage" | "werkstatt" | "aufmass";

export type MontagePackTab =
  | { key: "deckblatt"; kind: "deckblatt"; label: string }
  | { key: "ab"; kind: "ab"; label: string }
  | { key: string; kind: "aufmassblatt"; label: string; sheetId: number }
  | { key: string; kind: "stueckliste"; label: string; stuecklisteId: number }
  | { key: string; kind: "file"; label: string; item: Montageunterlage };

function stuecklisteTabLabel(sl: Stueckliste, pack: DocumentPackKind): string {
  const nr = stuecklisteDisplayNumber(sl);
  const isDraft = String(sl.status || "").toLowerCase() !== "fertig";
  let base: string;
  if (nr && !nr.startsWith("Stückliste #")) {
    base = nr.startsWith("SL") ? nr : `Stückliste ${nr}`;
  } else {
    const bauteil = String(sl.bauteil || "").trim();
    base = bauteil ? `Stückliste · ${bauteil}` : nr;
  }
  if (pack === "montage" && isWerkstattStueckliste(sl)) {
    base = `Werkstatt · ${base}`;
  }
  return isDraft ? `${base} (Entwurf)` : base;
}

function fileTabLabel(item: Montageunterlage, pack: DocumentPackKind): string {
  const title = item.title || item.filename || "Datei";
  if (pack === "montage" && isWerkstattPackItem(item)) {
    return `Werkstatt · ${title}`;
  }
  return title;
}

/**
 * Pack-Reihenfolge:
 * - Montage: Deckblatt → AB → Stücklisten → Dateien (Montage + Werkstatt)
 * - Werkstatt: Deckblatt → Stücklisten → Werkstatt-Dateien (keine AB)
 */
export function buildDocumentPackTabs(
  items: Montageunterlage[],
  stuecklisten: Stueckliste[],
  pack: DocumentPackKind = "montage",
  options: { hasAuftragsbestaetigung?: boolean } = {},
): MontagePackTab[] {
  const tabs: MontagePackTab[] = [
    { key: "deckblatt", kind: "deckblatt", label: "Deckblatt" },
  ];
  if (pack === "montage" && options.hasAuftragsbestaetigung) {
    tabs.push({ key: "ab", kind: "ab", label: "Auftragsbestätigung" });
  }

  const linkedMontageIds = new Set<number>();
  for (const sl of stuecklisten) {
    if (sl.montageunterlage_id != null) linkedMontageIds.add(sl.montageunterlage_id);
  }

  const latestSl = pickLatestStuecklisten(stuecklisten)
    .filter((sl) => {
      if (pack === "aufmass") return false;
      return pack === "werkstatt" ? isWerkstattStueckliste(sl) : true;
    })
    .slice()
    .sort((a, b) => {
      const an = stuecklisteDisplayNumber(a);
      const bn = stuecklisteDisplayNumber(b);
      if (an && bn && an !== bn) return an.localeCompare(bn, "de");
      const at = a.finalized_at || a.created_at || "";
      const bt = b.finalized_at || b.created_at || "";
      return at.localeCompare(bt);
    });

  for (const sl of latestSl) {
    tabs.push({
      key: `sl-${sl.id}`,
      kind: "stueckliste",
      label: stuecklisteTabLabel(sl, pack),
      stuecklisteId: sl.id,
    });
  }

  const files = items
    .filter((item) => {
      if (item.entry_type !== "file" || linkedMontageIds.has(item.id)) return false;
      if (pack === "werkstatt") return isWerkstattPackItem(item);
      if (pack === "aufmass") return isAufmassPackItem(item);
      return !isAufmassPackItem(item);
    })
    .slice()
    .sort((a, b) => {
      const aWz = isWerkstattPackItem(a) ? 1 : 0;
      const bWz = isWerkstattPackItem(b) ? 1 : 0;
      if (aWz !== bWz) return aWz - bWz;
      return String(a.created_at || "").localeCompare(String(b.created_at || ""));
    });

  for (const item of files) {
    tabs.push({
      key: `file-${item.id}`,
      kind: "file",
      label: fileTabLabel(item, pack),
      item,
    });
  }

  return tabs;
}

/** @deprecated Nutze buildDocumentPackTabs(..., "montage") */
export function buildMontagePackTabs(
  items: Montageunterlage[],
  stuecklisten: Stueckliste[],
  options: { hasAuftragsbestaetigung?: boolean } = {},
): MontagePackTab[] {
  return buildDocumentPackTabs(items, stuecklisten, "montage", options);
}

export function montageNotesAndAddresses(
  items: Montageunterlage[],
  packFilter?: DocumentPackKind | "all",
): {
  notes: Montageunterlage[];
  addresses: Montageunterlage[];
} {
  const filtered =
    packFilter === "werkstatt"
      ? items.filter(isWerkstattPackItem)
      : packFilter === "aufmass"
        ? items.filter(isAufmassPackItem)
        : packFilter === "montage"
          ? items.filter((item) => !isWerkstattPackItem(item) && !isAufmassPackItem(item))
          : items.filter((item) => !isAufmassPackItem(item));
  const notes = filtered
    .filter((item) => item.entry_type === "note")
    .slice()
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  const addresses = filtered
    .filter((item) => item.entry_type === "address")
    .slice()
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  return { notes, addresses };
}
